import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MeshGroup } from '../../types/mesh.ts';

// Icons
const ChevronIcon: React.FC<{ expanded: boolean; size?: number }> = ({ expanded, size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="currentColor"
    className={`transform transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`}
  >
    <path
      fillRule="evenodd"
      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const FolderIcon: React.FC<{ open?: boolean; size?: number }> = ({ open, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor" className="text-lime-400 shrink-0">
    {open ? (
      <path
        fillRule="evenodd"
        d="M2 6a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1H8a3 3 0 00-2.83 2H2V6zm0 5a1 1 0 011-1h5a1 1 0 01.95.68l1.5 4.32A1 1 0 019.5 16H3a1 1 0 01-1-1v-4zm16-1a1 1 0 00-1-1h-5a1 1 0 00-.95.68l-1.5 4.32A1 1 0 0010.5 16H17a1 1 0 001-1v-4z"
        clipRule="evenodd"
      />
    ) : (
      <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
    )}
  </svg>
);

const PlusIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
      clipRule="evenodd"
    />
  </svg>
);

const TrashIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
      clipRule="evenodd"
    />
  </svg>
);

interface MeshGroupTreeProps {
  meshGroups: MeshGroup[];
  onAddGroup: (parentGroupId?: string | null) => void;
  onRenameGroup: (uuid: string, name: string) => void;
  onDeleteGroup: (uuid: string) => void;
  onDropLayerToGroup: (layerUuid: string, groupId: string | null) => void;
  onDropGroupToGroup?: (draggedGroupId: string, targetGroupId: string | null) => void;
  renderLayerItem: (layerUuid: string) => React.ReactNode;
  getLayersForGroup: (groupId: string | null) => string[];
  disabled?: boolean;
}

interface GroupNodeProps {
  group: MeshGroup;
  depth: number;
  expandedGroups: Set<string>;
  toggleExpand: (uuid: string) => void;
  editingGroupId: string | null;
  editingName: string;
  setEditingGroupId: (id: string | null) => void;
  setEditingName: (name: string) => void;
  onRenameGroup: (uuid: string, name: string) => void;
  onDeleteGroup: (uuid: string) => void;
  onAddGroup: (parentGroupId?: string | null) => void;
  onDropLayerToGroup: (layerUuid: string, groupId: string | null) => void;
  renderLayerItem: (layerUuid: string) => React.ReactNode;
  getLayersForGroup: (groupId: string | null) => string[];
  disabled?: boolean;
}

const GroupNode: React.FC<GroupNodeProps> = ({
  group,
  depth,
  expandedGroups,
  toggleExpand,
  editingGroupId,
  editingName,
  setEditingGroupId,
  setEditingName,
  onRenameGroup,
  onDeleteGroup,
  onAddGroup,
  onDropLayerToGroup,
  renderLayerItem,
  getLayersForGroup,
  disabled,
}) => {
  const { t } = useTranslation();
  const isExpanded = expandedGroups.has(group.uuid);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const layerUuids = getLayersForGroup(group.uuid);

  useEffect(() => {
    if (editingGroupId === group.uuid && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingGroupId, group.uuid]);

  const handleSaveRename = () => {
    if (editingName.trim()) {
      onRenameGroup(group.uuid, editingName.trim());
    }
    setEditingGroupId(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (disabled) return;
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const layerUuid = e.dataTransfer.getData('text/layer-uuid');
    if (layerUuid) {
      onDropLayerToGroup(layerUuid, group.uuid);
    }
  };

  return (
    <div>
      {/* Group header */}
      <div
        className={`flex items-center gap-1 py-1 px-1 cursor-pointer hover:bg-stone-800 transition-colors ${
          isDragOver ? 'bg-lime-400/20 border border-lime-400 border-dashed' : ''
        }`}
        style={{ paddingLeft: `${depth * 16 + 4}px` }}
        onClick={() => toggleExpand(group.uuid)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onDoubleClick={(e) => {
          if (!disabled) {
            e.stopPropagation();
            setEditingGroupId(group.uuid);
            setEditingName(group.name);
          }
        }}
      >
        <ChevronIcon expanded={isExpanded} />
        <FolderIcon open={isExpanded} />

        {editingGroupId === group.uuid ? (
          <input
            ref={inputRef}
            type="text"
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            onBlur={handleSaveRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveRename();
              if (e.key === 'Escape') setEditingGroupId(null);
            }}
            className="text-sm bg-black border border-lime-500 rounded px-1 py-0 text-white focus:outline-none flex-1 min-w-0"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className="text-sm text-gray-200 truncate flex-1" title={group.name}>
            {group.name}
          </span>
        )}

        {!disabled && (
          <div className="flex items-center gap-1 ml-auto shrink-0" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => onAddGroup(group.uuid)}
              className="p-0.5 text-gray-500 hover:text-lime-400 transition-colors"
              title={t('meshGroups.addSubgroup')}
            >
              <PlusIcon size={12} />
            </button>
            <button
              onClick={() => onDeleteGroup(group.uuid)}
              className="p-0.5 text-gray-500 hover:text-red-400 transition-colors"
              title={t('meshGroups.deleteGroup')}
            >
              <TrashIcon size={12} />
            </button>
          </div>
        )}
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div>
          {/* Subgroups */}
          {group.subgroups.map((subgroup) => (
            <GroupNode
              key={subgroup.uuid}
              group={subgroup}
              depth={depth + 1}
              expandedGroups={expandedGroups}
              toggleExpand={toggleExpand}
              editingGroupId={editingGroupId}
              editingName={editingName}
              setEditingGroupId={setEditingGroupId}
              setEditingName={setEditingName}
              onRenameGroup={onRenameGroup}
              onDeleteGroup={onDeleteGroup}
              onAddGroup={onAddGroup}
              onDropLayerToGroup={onDropLayerToGroup}
              renderLayerItem={renderLayerItem}
              getLayersForGroup={getLayersForGroup}
              disabled={disabled}
            />
          ))}

          {/* Layers in this group */}
          {layerUuids.map((uuid) => (
            <div key={uuid} style={{ paddingLeft: `${(depth + 1) * 16 + 4}px` }}>
              {renderLayerItem(uuid)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const MeshGroupTree: React.FC<MeshGroupTreeProps> = ({
  meshGroups,
  onAddGroup,
  onRenameGroup,
  onDeleteGroup,
  onDropLayerToGroup,
  renderLayerItem,
  getLayersForGroup,
  disabled,
}) => {
  const { t } = useTranslation();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [isDragOverRoot, setIsDragOverRoot] = useState(false);

  const toggleExpand = useCallback((uuid: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  }, []);

  const handleRootDragOver = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOverRoot(true);
  };

  const handleRootDragLeave = () => {
    if (disabled) return;
    setIsDragOverRoot(false);
  };

  const handleRootDrop = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOverRoot(false);
    const layerUuid = e.dataTransfer.getData('text/layer-uuid');
    if (layerUuid) {
      onDropLayerToGroup(layerUuid, null);
    }
  };

  const rootLayerUuids = getLayersForGroup(null);

  return (
    <div className="text-sm">
      {/* Add group button */}
      {!disabled && (
        <div className="flex items-center px-2 py-1">
          <button
            onClick={() => onAddGroup(null)}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-lime-400 transition-colors"
          >
            <PlusIcon size={12} />
            <span>{t('meshGroups.addGroup')}</span>
          </button>
        </div>
      )}

      {/* Groups */}
      {meshGroups.map((group) => (
        <GroupNode
          key={group.uuid}
          group={group}
          depth={0}
          expandedGroups={expandedGroups}
          toggleExpand={toggleExpand}
          editingGroupId={editingGroupId}
          editingName={editingName}
          setEditingGroupId={setEditingGroupId}
          setEditingName={setEditingName}
          onRenameGroup={onRenameGroup}
          onDeleteGroup={onDeleteGroup}
          onAddGroup={onAddGroup}
          onDropLayerToGroup={onDropLayerToGroup}
          renderLayerItem={renderLayerItem}
          getLayersForGroup={getLayersForGroup}
          disabled={disabled}
        />
      ))}

      {/* Root (ungrouped) layers drop zone */}
      <div
        className={`min-h-[24px] transition-colors ${isDragOverRoot ? 'bg-lime-400/20 border border-lime-400 border-dashed' : ''}`}
        onDragOver={handleRootDragOver}
        onDragLeave={handleRootDragLeave}
        onDrop={handleRootDrop}
      >
        {isDragOverRoot && rootLayerUuids.length === 0 && (
          <div className="text-xs text-gray-500 text-center py-1">{t('meshGroups.dropHereForRoot')}</div>
        )}
        {rootLayerUuids.map((uuid) => (
          <div key={uuid}>{renderLayerItem(uuid)}</div>
        ))}
      </div>
    </div>
  );
};
